import { useEffect, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
// Deep imports, not the package barrels. `@attendance/shared`'s barrel
// `export *`s all 26 zod schema modules, and rollup can't drop a zod schema's
// top-level `z.object(...)` as side-effect-free — so importing one function
// through it put ~160 KB of schemas plus zod itself in the entry chunk. The
// `@attendance/ui` barrel is the same story via Dialog's CJS focus-trap.
import { checkAppVersion, type VersionCheckResult } from '@attendance/shared/version';
import { UpdateRequiredScreen } from '@attendance/ui/components/UpdateRequiredScreen';
import { API_BASE } from '../lib/api.js';

/**
 * Wraps the app and enforces "you must be on a supported version". On mount it
 * asks the server (GET /api/system/version) for the minimum client version and
 * compares it against this build's `__APP_VERSION__`. If we're too old, it
 * renders a blocking update screen instead of the app; otherwise the app
 * renders normally.
 *
 * The check FAILS OPEN — any network/parse error leaves the app usable (see
 * `checkAppVersion`). While the (fast) check is in flight we render the app
 * rather than a spinner: gating is the rare exception, so we don't want to
 * delay the common case behind a round-trip.
 */
export function VersionGate({ children }: { children: ReactNode }): JSX.Element {
  const { t } = useTranslation();
  const [result, setResult] = useState<VersionCheckResult | null>(null);

  useEffect(() => {
    let active = true;
    void checkAppVersion({
      versionUrl: `${API_BASE}/system/version`,
      currentVersion: __APP_VERSION__,
    }).then((r) => {
      if (active) setResult(r);
    });
    return () => {
      active = false;
    };
  }, []);

  if (result?.blocked) {
    return (
      <UpdateRequiredScreen
        currentVersion={result.currentVersion}
        minVersion={result.minVersion}
        updateUrl={result.updateUrl}
        labels={{
          title: t('versionGate.title'),
          description: t('versionGate.description'),
          currentLabel: t('versionGate.currentLabel'),
          requiredLabel: t('versionGate.requiredLabel'),
          updateCta: t('versionGate.updateCta'),
        }}
      />
    );
  }

  return <>{children}</>;
}
